import { useTranslation } from "react-i18next";
import { ArrowUpFromLine, Locate, SquarePen, Loader2 } from "lucide-react";
import { MiniMapHeaderProps } from "./types";

const baseButtonStyles =
  "text-[10px] leading-4 px-2 py-1 rounded border transition-colors disabled:cursor-not-allowed";

const activeButtonStyles = "bg-blue-100 border-blue-300 text-blue-700";

const inactiveButtonStyles =
  "bg-white border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900";

const disabledButtonStyles = "bg-gray-100 border-gray-200 text-gray-400";

export const MiniMapHeader = ({
  minimapSize,
  showAdminTools,
  setMinimapSize,
  onEditAlignment,
  onEditFrameState,
  onUpdatePreviewImage,
  isEditingAlignment,
  isEditingFrameState,
  isUploadingPreviewImage,
  isMinimapImageLoaded,
}: MiniMapHeaderProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex justify-between items-center px-3 py-2 bg-gray-50 border-b border-gray-200">
      <div className="text-gray-700 text-xs font-medium whitespace-nowrap truncate">
        {t("doc_viewer.minimap_title")}
      </div>
      <div className="flex items-center gap-1">
        {showAdminTools && minimapSize === "large" && (
          <div className="flex items-center gap-1 mr-1">
            <button
              type="button"
              onClick={onEditAlignment}
              className={`${baseButtonStyles}${
                isEditingAlignment
                  ? ` ${activeButtonStyles}`
                  : ` ${inactiveButtonStyles}`
              }`}
              title={t("doc_viewer.edit_alignment")}
            >
              <SquarePen className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onEditFrameState}
              className={`${baseButtonStyles}${
                isEditingFrameState
                  ? ` ${activeButtonStyles}`
                  : ` ${inactiveButtonStyles}`
              }`}
              title={t("doc_viewer.edit_frame_position")}
            >
              <Locate className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onUpdatePreviewImage}
              disabled={isUploadingPreviewImage || !isMinimapImageLoaded}
              className={`${baseButtonStyles}${
                isUploadingPreviewImage
                  ? ` ${activeButtonStyles}`
                  : !isMinimapImageLoaded
                    ? ` ${disabledButtonStyles}`
                    : ` ${inactiveButtonStyles}`
              }`}
              title={t("doc_viewer.update_floor_plan_preview")}
            >
              {isUploadingPreviewImage ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUpFromLine className="h-4 w-4" />
              )}
            </button>
          </div>
        )}
        <button
          onClick={setMinimapSize}
          className="text-gray-600 hover:text-gray-900 hover:bg-gray-200 p-1 rounded transition-colors"
          title={t("doc_viewer.toggle_minimap_size")}
          type="button"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={
                minimapSize === "normal"
                  ? "M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                  : "M5 15l7-7 7 7"
              }
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
